/**
 * GestionCasillas — conectar/verificar/probar las casillas de email de la marca.
 *
 * Es el MISMO componente que usa la página dedicada (`/activa/email`) y el paso de email del
 * wizard: WhatsApp ya se conectaba dentro del flujo y el email mandaba a otra pantalla, así que
 * el operador salía del wizard para configurar la casilla. Extraído acá, la casilla se da de alta
 * SIN salir del flujo y la página y el wizard comparten un solo código —no dos formularios SMTP
 * que se desincronizan.
 *
 * `embebido` recorta el cromo (sin encabezado propio ni el pie con el link a WhatsApp): eso lo
 * pone quien lo usa. La lógica —alta, verificación real, prueba, predeterminada— es idéntica.
 *
 * Regla: guardada ≠ puede enviar. Una cuenta sólo queda "verificada" si la conexión SMTP real
 * respondió (nodemailer.verify contra el servidor de verdad).
 */

import { useCallback, useEffect, useState } from "react";
import {
  Mail, Plus, Loader2, AlertTriangle, CheckCircle2, Trash2, Send,
  ShieldCheck, Star, X, RefreshCw,
} from "lucide-react";
import { activaApi } from "../api";
import { num } from "../utils/format";
import type { CuentaEmailReal, EstadoCuentaEmail } from "../types.real";

/** Presets de los proveedores que usa la gente acá: evita el 90% de los errores de puerto. */
const PRESETS: { label: string; host: string; port: number; secure: boolean }[] = [
  { label: "Hostinger", host: "smtp.hostinger.com", port: 587, secure: false },
  { label: "Gmail / Workspace", host: "smtp.gmail.com", port: 587, secure: false },
  { label: "Outlook / M365", host: "smtp.office365.com", port: 587, secure: false },
  { label: "Zoho", host: "smtp.zoho.com", port: 587, secure: false },
];

const ESTADO: Record<EstadoCuentaEmail, { label: string; cls: string }> = {
  sin_verificar: { label: "Sin verificar", cls: "bg-amber-50 text-amber-700" },
  verificando: { label: "Verificando…", cls: "bg-sky-50 text-sky-600" },
  verificada: { label: "Verificada", cls: "bg-emerald-50 text-emerald-700" },
  error: { label: "Con error", cls: "bg-red-50 text-red-600" },
};

const VACIO = { nombre: "", fromEmail: "", fromName: "", smtpHost: "", smtpPort: 587, smtpSecure: false, smtpUser: "", smtpPass: "" };

interface Props {
  /** true = dentro del wizard: sin el título grande ni el pie con el link a WhatsApp. */
  embebido?: boolean;
  /** true = arranca con el formulario de alta abierto (lo usa el botón del header de la página). */
  abrirAltaAlMontar?: boolean;
  /** se dispara cuando cambia el estado de las casillas, para que el padre revalide el envío. */
  onCambio?: () => void;
}

export default function GestionCasillas({ embebido = false, abrirAltaAlMontar = false, onCambio }: Props) {
  const [cuentas, setCuentas] = useState<CuentaEmailReal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState<typeof VACIO | null>(abrirAltaAlMontar ? { ...VACIO } : null);
  const [probar, setProbar] = useState<{ id: string; to: string } | null>(null);
  const [aviso, setAviso] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await activaApi.emailAccounts();
      setCuentas(r?.data || []);
    } catch (e: any) {
      setError(e?.message || "No pudimos conectar con el servidor. Probá de nuevo en un rato.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (k: string, fn: () => Promise<void>) => {
    setBusy(k); setError(""); setAviso("");
    try { await fn(); onCambio?.(); } catch (e: any) { setError(e?.message || "No se pudo completar la acción"); }
    finally { setBusy(""); }
  };

  const guardar = () => accion("guardar", async () => {
    if (!form) return;
    const r = await activaApi.createEmailAccount(form as any);
    if (r.status === 422) { setError(r.error || "Revisá los datos"); return; }
    setForm(null);
    await cargar();
    // guardarla no alcanza: se verifica sola para que el operador sepa YA si sirve
    if (r.data?.id) await verificar(r.data.id);
  });

  const verificar = async (id: string) => accion("verify-" + id, async () => {
    const r = await activaApi.verifyEmailAccount(id);
    await cargar();
    setAviso(r.data?.ok ? "✓ La casilla quedó verificada: las campañas ya pueden salir desde acá." : "");
    if (r.data && !r.data.ok) setError("No pudimos conectar: " + (r.data.error || "revisá servidor, usuario y contraseña"));
  });

  // Borra el documento de verdad, con sus credenciales SMTP: se confirma ANTES de `accion`, que
  // marca busy y limpia los avisos; confirmar adentro dejaba esos efectos aunque se cancelara.
  const eliminar = (c: CuentaEmailReal) => {
    const aviso =
      `¿Eliminar la casilla "${c.nombre}" (${c.fromEmail})? Se borran sus credenciales SMTP y hay que volver a cargarlas para usarla.` +
      (c.predeterminada ? " Es la predeterminada: las campañas pasan a salir desde otra casilla." : "");
    if (!window.confirm(aviso)) return;
    return accion("del-" + c.id, async () => {
      await activaApi.deleteEmailAccount(c.id);
      await cargar();
    });
  };

  const hacerPredeterminada = (id: string) => accion("pred-" + id, async () => {
    await activaApi.updateEmailAccount(id, { predeterminada: true });
    await cargar();
  });

  const enviarPrueba = () => accion("test", async () => {
    if (!probar) return;
    const r = await activaApi.testSendEmailAccount(probar.id, probar.to);
    if (r.data?.ok) { setAviso(`✓ Mail de prueba enviado a ${probar.to}. Revisá la bandeja (y el spam).`); setProbar(null); }
    else setError("No se pudo enviar: " + (r.data?.error || r.error || "revisá la configuración"));
    await cargar();
  });

  const usarPreset = (p: typeof PRESETS[number]) =>
    setForm((f) => (f ? { ...f, smtpHost: p.host, smtpPort: p.port, smtpSecure: p.secure } : f));

  const hayVerificada = cuentas.some((c) => c.estado === "verificada");

  return (
    <div className="space-y-4">
      {/* En modo embebido el botón de alta va acá arriba (la página tiene el suyo en el header) */}
      {/* Sin casillas, el estado vacío de abajo ya tiene su botón: dos «Conectar casilla» juntos confundían. */}
      {embebido && !form && cuentas.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {hayVerificada ? "Tu casilla está lista: los mails salen desde tu dirección." : "Conectá tu casilla para que los mails salgan desde tu dirección, no desde una de Deenex."}
          </p>
          <button
            onClick={() => setForm({ ...VACIO })}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors flex-shrink-0"
          >
            <Plus size={14} /> Conectar casilla
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {aviso && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800 flex items-start gap-2">
          <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
          <span>{aviso}</span>
        </div>
      )}

      {/* Alta de casilla */}
      {form && (
        <div className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-base font-bold text-slate-900">Conectar una casilla</h2>
            <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-600" aria-label="Cancelar"><X size={18} /></button>
          </div>

          <p className="text-xs font-semibold text-slate-500 mb-2">¿Con quién tenés el correo?</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => usarPreset(p)}
                className={`px-3 h-8 rounded-full text-xs font-medium border transition-all ${
                  form.smtpHost === p.host ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {([
              { k: "fromEmail", l: "Tu email", ph: "hola@tunegocio.com", tipo: "email", ancho: "" },
              { k: "fromName", l: "Nombre del remitente", ph: "HATSU", tipo: "text", ancho: "" },
              { k: "smtpHost", l: "Servidor SMTP", ph: "smtp.tuproveedor.com", tipo: "text", ancho: "" },
              { k: "smtpUser", l: "Usuario SMTP", ph: "normalmente tu email", tipo: "text", ancho: "" },
              { k: "smtpPass", l: "Contraseña", ph: "••••••••", tipo: "password", ancho: "sm:col-span-2" },
            ] as const).map((f) => (
              <label key={f.k} className={`block ${f.ancho}`}>
                <span className="text-xs font-semibold text-slate-600">{f.l}</span>
                <input
                  type={f.tipo}
                  value={(form as any)[f.k] || ""}
                  placeholder={f.ph}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                />
              </label>
            ))}
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Puerto</span>
              <input
                type="number"
                value={form.smtpPort}
                onChange={(e) => setForm({ ...form, smtpPort: Number(e.target.value), smtpSecure: Number(e.target.value) === 465 })}
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">587 es lo más común · 465 si tu proveedor pide SSL</span>
            </label>
          </div>

          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3 flex items-start gap-2">
            <ShieldCheck size={15} className="text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              La contraseña se guarda <strong>cifrada</strong> y nunca vuelve a salir del servidor. Si usás Gmail o Outlook con
              2 pasos, generá una <strong>contraseña de aplicación</strong> — la de tu cuenta no va a funcionar.
            </p>
          </div>

          <button
            onClick={guardar}
            disabled={busy === "guardar" || !form.fromEmail || !form.smtpHost || !form.smtpPass}
            className="mt-4 w-full h-11 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy === "guardar" ? <><Loader2 size={15} className="animate-spin" /> Guardando y probando la conexión…</> : <>Guardar y verificar</>}
          </button>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-10 text-center">
          <Loader2 size={22} className="animate-spin text-violet-500 mx-auto" />
        </div>
      ) : cuentas.length === 0 && !form ? (
        <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-violet-600 text-white flex items-center justify-center mx-auto"><Mail size={22} /></div>
          <p className="text-lg font-bold text-slate-900 mt-3">Todavía no conectaste ninguna casilla</p>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Sin una casilla conectada, las campañas por email no pueden salir.
          </p>
          <button onClick={() => setForm({ ...VACIO })} className="inline-flex items-center gap-2 h-11 px-5 mt-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
            <Plus size={16} /> Conectar casilla
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {cuentas.map((c) => {
            const est = ESTADO[c.estado] || ESTADO.sin_verificar;
            return (
              <div key={c.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.estado === "verificada" ? "bg-emerald-50 text-emerald-600" : "bg-slate-50 text-slate-400"}`}>
                      <Mail size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-800 truncate">{c.nombre}</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${est.cls}`}>{est.label}</span>
                        {c.predeterminada && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 inline-flex items-center gap-1">
                            <Star size={9} /> En uso
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{c.fromEmail}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5 font-mono truncate">{c.smtpHost}:{c.smtpPort}</p>
                      {c.estado === "verificada" && (
                        <p className="text-[11px] text-slate-400 mt-1 tabular-nums">{num(c.enviadosHoy)}/{num(c.msgsPorDia)} mails hoy</p>
                      )}
                      {c.lastError && c.estado === "error" && (
                        <p className="text-[11px] text-red-600 mt-1 leading-snug">{c.lastError}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => verificar(c.id)} disabled={!!busy} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5">
                      {busy === "verify-" + c.id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Verificar
                    </button>
                    <button onClick={() => setProbar({ id: c.id, to: "" })} disabled={!!busy} className="h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5">
                      <Send size={12} /> Probar
                    </button>
                    {!c.predeterminada && (
                      <button onClick={() => hacerPredeterminada(c.id)} disabled={!!busy} className="h-8 px-2.5 rounded-lg border border-violet-200 bg-white text-[11px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                        Usar esta
                      </button>
                    )}
                    <button onClick={() => eliminar(c)} disabled={!!busy} className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-red-600 hover:border-red-200 disabled:opacity-50 inline-flex items-center justify-center" aria-label="Eliminar">
                      {busy === "del-" + c.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={13} />}
                    </button>
                  </div>
                </div>

                {/* Prueba de envío real por esta casilla */}
                {probar?.id === c.id && (
                  <div className="mt-3 pt-3 border-t border-slate-50 flex items-center gap-2 flex-wrap">
                    <input
                      type="email"
                      autoFocus
                      value={probar.to}
                      placeholder="tu-email@para-probar.com"
                      onChange={(e) => setProbar({ ...probar, to: e.target.value })}
                      className="flex-1 min-w-[200px] h-9 rounded-lg border border-slate-200 px-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                    />
                    <button onClick={enviarPrueba} disabled={!probar.to || busy === "test"} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1.5">
                      {busy === "test" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Mandar prueba
                    </button>
                    <button onClick={() => setProbar(null)} className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50">Cancelar</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Estado del canal: lo que importa saber antes de mandar una campaña */}
      {!loading && cuentas.length > 0 && !hayVerificada && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Ninguna casilla está verificada todavía.</strong> Guardar la cuenta no alcanza: hasta que la conexión no
            dé bien, las campañas por email no van a salir.
          </p>
        </div>
      )}
    </div>
  );
}
